import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAuth } from "../../auth/AuthProvider";
import airtable from "../../lib/airtables";
import { setMessagesUnread } from "../../redux/app/actions";
import { teacherFieldIds } from "../../lib/airtables/tables/Teachers";
import allResourcesImg from "../../assets/images/allresources.png";
import friendsOfDwfImg from "../../assets/images/friendsofdwf.png";
import secretPathResourcesImg from "../../assets/images/secretpathresources.png";
import legacyschoolstoolkitImg from "../../assets/images/legacyschoolstoolkit.png";
import virtuallegacyspaceImg from "../../assets/images/virtuallegacyspace.png";
import collectionsImg from "../../assets/images/collections.png";
import facebookIcon from "../../assets/icons/Facebook.png";
import instagramIcon from "../../assets/icons/Instagram.png";
import linkedInIcon from "../../assets/icons/LinkedIn.png";
import xIcon from "../../assets/icons/x.png";
import youtubeIcon from "../../assets/icons/YouTube.png";

const dashboardResourceTiles = [
  {
    title: "Secret Path",
    to: "/app/secret-path",
    imageSrc: secretPathResourcesImg,
  },
  {
    title: "Legacy Schools Toolkit",
    to: "/app/legacy-schools-toolkit",
    imageSrc: legacyschoolstoolkitImg,
  },
  {
    title: "Virtual Legacy Space",
    to: "https://virtuallegacyspace.ca/", 
    imageSrc: virtuallegacyspaceImg,
  },
  {
    title: "Friends of DWF",
    to: "/app/friends-of-dwf",
    imageSrc: friendsOfDwfImg,
  },
  {
    title: "Collections",
    to: "/app/collections",
    imageSrc: collectionsImg,
  },
   {
    title: "All Resources",
    to: "/app/all-resources",
    imageSrc: allResourcesImg,
  },
] as const;

const socialLinks = [
  {
    name: "Facebook",
    href: "https://www.facebook.com/downiewenjack",
    iconSrc: facebookIcon,
  },
  {
    name: "X",
    href: "https://twitter.com/downiewenjack",
    iconSrc: xIcon,
  },
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/company/downiewenjack",
    iconSrc: linkedInIcon,
  },
  {
    name: "YouTube",
    href: "https://www.youtube.com/@DownieWenjack",
    iconSrc: youtubeIcon,
  },
  {
    name: "Instagram",
    href: "https://www.instagram.com/downiewenjack/",
    iconSrc: instagramIcon,
  },
] as const;

export default function DashboardPage() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const location = useLocation();
  const [showWelcome, setShowWelcome] = useState(false);

  // Show the post-registration welcome banner once, then clear the
  // navigation state so a refresh doesn't re-show it.
  useEffect(() => {
    if (location.state?.welcome) {
      setShowWelcome(true);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  useEffect(() => {
    const recipientEmail = user?.[teacherFieldIds.email];
    if (!recipientEmail) {
      dispatch(setMessagesUnread(false));
      return;
    }

    airtable.messages.getUnreadForRecipientEmail(recipientEmail).then((items) => {
      dispatch(setMessagesUnread(items.length > 0));
    }).catch(() => {
      dispatch(setMessagesUnread(false));
    });
  }, [dispatch, user]);

  return (
    <main className="dashboard-page page bg-white">
      <div className="max-w-7xl mx-auto py-12 px-4">
        {showWelcome ? (
          <div className="mb-8 rounded-xl border border-green-200 bg-green-50 p-5 text-green-800">
            <h3 className="text-xl font-semibold">{`Welcome, ${user?.[teacherFieldIds.name] || ""}!`}</h3>
            <p className="mt-1">
              Your account is ready! Explore the resources below to get started.
            </p>
          </div>
        ) : null}
        {user?.[teacherFieldIds.artistAmbassadorWinner] ? (
          <Link
            to="/app/artist-ambassador-events"
            className="mb-8 block rounded-xl bg-gradient-to-r token-gradient-primary p-5 text-white shadow-sm hover:shadow-md transition-shadow"
          >
            <h3 className="text-xl font-semibold">
              Congratulations! Your classroom has been selected to participate in an Artist Ambassador Workshop!
            </h3>
            <p className="mt-1">
              Click here to select the artist, date, and time.
            </p>
          </Link>
        ) : null}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_40%] gap-12">
          {/* Quick Jump Section */}
          <div className="flex h-full flex-col rounded-[32px] border-2 border-white bg-white p-6 lg:items-center lg:p-8">
            <div className="w-full max-w-[420px] sm:max-w-[500px] lg:max-w-[520px]">
              <h1 className="flex text-5xl font-semibold text-gray-800 portrait:text-3xl lg:text-5xl md:text-5xl sm:text-3xl">
              Dashboard</h1>
              <p className="text-gray-500 mb-6 py-6">Boozhoo! Welcome to The HUB! This is the place to find the resources you need to move reconciliation forward in your learning space.</p>
            </div>
            <div className="mt-auto w-full max-w-[420px] pb-5 sm:max-w-[500px] lg:max-w-[520px]">
              <div className="grid grid-cols-1 gap-3 border-b border-gray-200 pb-5 sm:grid-cols-2">
              {dashboardResourceTiles.map((tile) => (
                <Link
                  key={tile.title}
                  to={tile.to}
                  className="group relative aspect-square overflow-hidden rounded-[24px] border border-[#e7deef] bg-white shadow-[0_10px_24px_rgba(54,36,81,0.08)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_16px_32px_rgba(54,36,81,0.14)]"
                >
                  <img
                    src={tile.imageSrc}
                    alt={tile.title}
                    className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/45 transition duration-300 group-hover:bg-black/35" />
                  <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-white">
                    <h3
                      className="text-[1.9rem] leading-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)] sm:text-[2.2rem] lg:text-[2.4rem]"
                      style={{ fontFamily: "loveletterregular, serif" }}
                    >
                      {tile.title}
                    </h3>
                  </div>
                </Link>
              ))}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                   {/* Primary Link */}
                    <Link 
                      to="/app/submit-a-reconciliaction"
                      className="inline-flex items-center justify-center px-4 py-2 bg-[#4F2874] text-white rounded-lg hover:bg-[#381954]">
                    Submit a ReconciliACTION to Win
                  </Link>

                  {/* Secondary Button */}
                  <a 
                    href="/app/resource-submission"
                    className="inline-flex items-center justify-center px-4 py-2 bg-[#c9edf6] text-gray-800 rounded-lg hover:bg-sky-200">
                    Submit a Resource
                  </a>
                </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            </div>

            {/* Useful Links */}
            <div className="token-surface-dashboard rounded-xl p-8">
              <h2 className="text-2xl font-semibold mb-2 text-gray-700">Quick Links</h2>
              <p className="text-gray-600 mb-6 italic"></p>

              <div className="space-y-3">
                <a
                  href="https://downiewenjack.ca/our-work/legacy-schools-programs/update-educator-contact-information/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-gray-700 token-link-primary"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Update my contact information
                </a>

                <a
                  href="https://downiewenjack.ca/our-work/youth-ambassador-program/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-gray-700 token-link-primary"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Youth Ambassador Program
                </a>

                <a
                  href="https://downiewenjack.ca/our-work/artist-ambassador-program/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-gray-700 token-link-primary"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Artist Ambassador Program
                </a>

                <a
                  href="https://downiewenjack.ca/events/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-gray-700 token-link-primary"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Events
                </a>

                <a
                  href="https://downiewenjack.ca/our-work/protocols/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-gray-700 token-link-primary"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Protocols for engaging with Indigenous people & communities
                </a>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between gap-4">
                  {socialLinks.map((link) => (
                    <a
                      key={link.name}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-12 w-12 items-center justify-center transition-transform duration-200 hover:scale-105"
                      aria-label={link.name}
                    >
                      <img src={link.iconSrc} alt={link.name} className="h-10 w-10 object-contain" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </main>
  );
}
